import { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronRight, AlertCircle, Loader2, Car, Bike } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WA_NUMBER = '919347381519';

const VehicleBatteryFinder = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dbReady, setDbReady] = useState(false);
  const [fetchStatus, setFetchStatus] = useState('ok');
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Warm up: verify both collections are accessible on mount
  useEffect(() => {
    const warmUp = async () => {
      try {
        const res = await fetch(`${API}/api/unified-battery-search?q=test`);
        const data = await res.json();
        if (data.status === 'ok' || data.status === 'empty_collection') {
          setDbReady(true);
          console.log("Unified Database Ready");
          console.log("Total Vehicles Loaded:", data.results ? data.results.length : 0, "| Cars:", data.car_count || 0, "| Bikes:", data.bike_count || 0);
        } else {
          console.log("DB Fetch Error: unified search returned status", data.status);
        }
      } catch (err) {
        console.log("DB Fetch Error:", err);
      }
    };
    warmUp();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchResults = useCallback(async (q) => {
    if (q.trim().length < 1) {
      setSuggestions([]);
      setShowDropdown(false);
      setFetchStatus('ok');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/unified-battery-search?q=${encodeURIComponent(q)}`);
      const data = await res.json();

      if (data.status === 'db_error') {
        console.log("DB Fetch Error: database query failed");
        setFetchStatus('db_error');
        setSuggestions([]);
      } else if (data.status === 'empty_collection') {
        console.log("DB Fetch Error: collections are empty");
        setFetchStatus('empty_collection');
        setSuggestions([]);
      } else {
        setFetchStatus('ok');
        setSuggestions(data.results || []);
        console.log("Total Vehicles Loaded:", (data.results || []).length, "| Cars:", data.car_count || 0, "| Bikes:", data.bike_count || 0);
      }
      setShowDropdown(true);
    } catch (err) {
      console.log("DB Fetch Error:", err);
      setFetchStatus('db_error');
      setSuggestions([]);
      setShowDropdown(true);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(val), 250);
  };

  const handleSelect = (item) => {
    const is4W = item.source === '4W';
    const label = is4W
      ? `${item.make} ${item.model} - ${item.fuel}`
      : `${item.make} ${item.model} - ${item.variant}`;
    setQuery(label);
    setSelected(item);
    setShowDropdown(false);
  };

  const handleClear = () => {
    setQuery('');
    setSelected(null);
    setSuggestions([]);
    setShowDropdown(false);
    setFetchStatus('ok');
  };

  const buildWhatsAppLink = (batteryName) => {
    const is4W = selected.source === '4W';
    const vehicleDesc = is4W
      ? `${selected.make} ${selected.model} ${selected.fuel}`
      : `${selected.make} ${selected.model} ${selected.variant}`;
    const msg = `Hi, I want to buy the ${batteryName} for my ${vehicleDesc}.`;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  };

  const getBatteryList = () => {
    if (!selected) return [];
    return selected.source === '4W'
      ? (selected.battery_models || [])
      : (selected.compatible_batteries || []);
  };

  const renderDropdownContent = () => {
    if (loading) {
      return <div className="px-5 py-4 text-gray-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching...</div>;
    }

    if (fetchStatus === 'db_error' || fetchStatus === 'empty_collection') {
      return (
        <div className="px-5 py-4 flex items-start gap-3" data-testid="db-error-msg">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-400 text-sm font-medium">Database connection error.</p>
            <p className="text-gray-500 text-xs mt-1">Please contact support.</p>
          </div>
        </div>
      );
    }

    if (suggestions.length > 0) {
      return suggestions.map((item) => {
        const is4W = item.source === '4W';
        const TypeIcon = is4W ? Car : Bike;
        const typeBadge = is4W ? item.fuel : item.variant;
        return (
          <button
            key={item.id}
            onClick={() => handleSelect(item)}
            className="w-full text-left px-5 py-3 transition flex items-center justify-between group"
            style={{ borderBottom: '1px solid #3A3A3A' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3A3A3A'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            data-testid={`suggestion-${item.id}`}
          >
            <span className="text-gray-200 text-sm sm:text-base flex items-center gap-2">
              <TypeIcon className="w-4 h-4 shrink-0" style={{ color: '#FFFFFF' }} />
              <span>
                <span className="font-semibold text-white">{item.make}</span>{' '}
                {item.model}
                {typeBadge && (
                  <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: '#3A3A3A', color: '#B0B0B0' }}>
                    {typeBadge}
                  </span>
                )}
              </span>
            </span>
            <ChevronRight className="w-4 h-4 text-gray-500 group-hover:text-green-400 transition" />
          </button>
        );
      });
    }

    return (
      <div className="px-5 py-4 text-gray-500 text-sm" data-testid="no-results-msg">
        No exact match found. Please verify the vehicle details.
      </div>
    );
  };

  const batteries = getBatteryList();

  return (
    <section className="py-16 px-4" style={{ backgroundColor: '#1A1A1A' }} data-testid="vehicle-battery-finder">
      <div className="max-w-3xl mx-auto">
        <h2 className="text-3xl font-bold text-white text-center mb-2" data-testid="finder-heading">
          Know Your Battery
        </h2>
        <p className="text-gray-400 text-center mb-8 text-sm sm:text-base">
          The ultimate fitment guide for your car or bike. Search by make, model, or type.
        </p>

        {/* Search */}
        <div className="relative" ref={wrapperRef}>
          <div className="relative">
            {!dbReady && !query ? (
              <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 animate-spin" />
            ) : (
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
            )}
            <input
              type="text"
              value={query}
              onChange={handleInputChange}
              onFocus={() => query.trim().length >= 1 && (suggestions.length > 0 || fetchStatus !== 'ok') && setShowDropdown(true)}
              placeholder="e.g. Seltos Petrol or Activa 5G"
              className="w-full pl-12 pr-12 py-4 rounded-xl text-white text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition placeholder-gray-500"
              style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }}
              data-testid="battery-search-input"
            />
            {query && (
              <button
                onClick={handleClear}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition"
                data-testid="search-clear-btn"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {showDropdown && (
            <div
              className="absolute left-0 right-0 mt-1 rounded-xl shadow-xl max-h-72 overflow-y-auto"
              style={{ zIndex: 9999, backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }}
              data-testid="search-dropdown"
            >
              {renderDropdownContent()}
            </div>
          )}
        </div>

        {/* Results Grid */}
        {selected && batteries.length > 0 && (
          <div className="mt-10" data-testid="battery-results">
            <p className="text-sm text-gray-400 mb-4">
              {batteries.length} compatible {batteries.length === 1 ? 'battery' : 'batteries'} for{' '}
              <span className="font-semibold text-white">
                {selected.make} {selected.model} ({selected.source === '4W' ? selected.fuel : selected.variant})
              </span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {batteries.map((name, idx) => (
                <div
                  key={idx}
                  className="rounded-xl shadow-md p-5 transition-all hover:shadow-lg"
                  style={{ backgroundColor: '#2E2E2E', border: '1px solid #3A3A3A' }}
                  data-testid={`battery-card-${idx}`}
                >
                  <p className="font-semibold text-white text-base leading-snug mb-4">{name}</p>
                  <a
                    href={buildWhatsAppLink(name)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block text-white font-semibold py-2 px-4 rounded-lg w-full mt-4 transition-colors text-center text-sm"
                    style={{ backgroundColor: '#25D366' }}
                    data-testid={`whatsapp-btn-${idx}`}
                  >
                    Check Price &amp; Buy on WhatsApp
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {selected && batteries.length === 0 && (
          <div className="mt-10 text-center text-gray-500 text-sm" data-testid="no-batteries-msg">
            No battery data available for this vehicle yet. Please contact us on WhatsApp for assistance.
          </div>
        )}
      </div>
    </section>
  );
};

export default VehicleBatteryFinder;
